import PageToolbar from 'ui/PageToolbar/PageToolbar';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { Table, Tag, Typography, Button, Input, Tooltip, Alert } from 'antd';
import {
  SearchOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ScissorOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { ColumnsType, TableProps } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import { InterviewerTasksOrderBy } from 'api/graphql-global-types';
import { AIClipListItem } from 'api/aiClips/types';
import {
  fetchClipRecordsPage,
  FetchClipRecordsPageResult,
} from 'api/aiClips/client';
import { AI_CLIP_DETAIL_GEN } from 'constants/routes';
import styles from './AIClips.module.scss';

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT_DIRECTION = 'DESC';
/** Cast until codegen picks the members up: the API has to deploy first. */
const INTERVIEW_NAME = 'interviewName' as InterviewerTasksOrderBy;
const AI_CLIPS_CREATED_AT = 'aiClipsCreatedAt' as InterviewerTasksOrderBy;

const DEFAULT_ORDER_BY = InterviewerTasksOrderBy.createdAt;

const SORTABLE_COLUMNS: Record<string, InterviewerTasksOrderBy> = {
  interviewName: INTERVIEW_NAME,
  guestName: InterviewerTasksOrderBy.intervieweeName,
  interviewCreatedAt: InterviewerTasksOrderBy.createdAt,
  clipsCreatedAt: AI_CLIPS_CREATED_AT,
};

/** First direction repeated so Ant Design 4 wraps its sort cycle instead of emitting cancel. */
const SORT_DIRECTIONS: SortOrder[] = ['descend', 'ascend', 'descend'];

function getSearchParams(searchTerm: string): Record<string, string> {
  const trimmed = searchTerm.trim();
  if (!trimmed) return {};

  const isUuidLike = /^[0-9a-f-]{8,36}$/i.test(trimmed);
  if (isUuidLike) return { id: trimmed };

  const isSlugLike = /^[a-z0-9]+(?:[.\-_][a-z0-9]+)+$/i.test(trimmed);
  if (isSlugLike) return { slugSearch: trimmed };

  return { nameSearch: trimmed };
}

const AIClips: React.FC = () => {
  const history = useHistory();
  const apolloClient = useApolloClient();
  const [records, setRecords] = useState<AIClipListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [orderBy, setOrderBy] =
    useState<InterviewerTasksOrderBy>(DEFAULT_ORDER_BY);
  const [direction, setDirection] = useState<'ASC' | 'DESC'>(
    DEFAULT_SORT_DIRECTION
  );

  const load = useCallback(
    async (
      currentPage: number,
      size: number,
      searchTerm: string,
      sortBy: InterviewerTasksOrderBy,
      sortDirection: 'ASC' | 'DESC'
    ) => {
      setLoading(true);
      setError(null);
      try {
        const data: FetchClipRecordsPageResult = await fetchClipRecordsPage(
          apolloClient,
          {
            limit: size,
            offset: (currentPage - 1) * size,
            direction: sortDirection,
            orderBy: sortBy,
            ...getSearchParams(searchTerm),
          }
        );
        setRecords(data.entities);
        setTotal(data.total);
      } catch (e: any) {
        setError(e?.message || 'Failed to load clips from the API.');
      } finally {
        setLoading(false);
      }
    },
    [apolloClient]
  );

  useEffect(() => {
    load(page, pageSize, appliedSearch, orderBy, direction);
  }, [load, page, pageSize, appliedSearch, orderBy, direction]);

  const handleSearch = useCallback(() => {
    setAppliedSearch(searchInput);
    setPage(1);
  }, [searchInput]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  const sortOrderFor = useCallback(
    (columnKey: string): SortOrder | undefined => {
      if (SORTABLE_COLUMNS[columnKey] !== orderBy) return undefined;
      return direction === 'ASC' ? 'ascend' : 'descend';
    },
    [orderBy, direction]
  );

  const handleTableChange = useCallback<
    NonNullable<TableProps<AIClipListItem>['onChange']>
  >((_pagination, _filters, sorter, extra) => {
    if (extra.action !== 'sort') return;

    const { columnKey, order } = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextOrderBy = SORTABLE_COLUMNS[String(columnKey)];
    if (!nextOrderBy || !order) return;

    setOrderBy(nextOrderBy);
    setDirection(order === 'ascend' ? 'ASC' : 'DESC');
    setPage(1);
  }, []);

  const columns: ColumnsType<AIClipListItem> = [
    {
      title: 'Interview',
      dataIndex: 'interviewName',
      key: 'interviewName',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sortOrderFor('interviewName'),
      render: (_: unknown, row: AIClipListItem) => (
        <Text strong>
          {row.interviewName || <Text type="secondary">—</Text>}
        </Text>
      ),
    },
    {
      title: 'Host Name',
      dataIndex: 'hostName',
      key: 'hostName',
      render: (_: unknown, row: AIClipListItem) => (
        <Text strong>{row.hostName || <Text type="secondary">—</Text>}</Text>
      ),
    },
    {
      title: 'Guest Name',
      dataIndex: 'guestName',
      key: 'guestName',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sortOrderFor('guestName'),
      render: (_: unknown, row: AIClipListItem) => (
        <Text strong>{row.guestName || <Text type="secondary">—</Text>}</Text>
      ),
    },
    {
      title: 'Interview ID',
      dataIndex: 'interviewId',
      key: 'interviewId',
      ellipsis: true,
      render: (id: string) => (
        <span className={styles.copyableCell}>
          <Tooltip title={id}>
            <Text code copyable={{ text: id }}>
              {id.length > 16 ? `${id.slice(0, 16)}…` : id}
            </Text>
          </Tooltip>
        </span>
      ),
    },
    {
      title: 'Interview created',
      dataIndex: 'interviewCreatedAt',
      key: 'interviewCreatedAt',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sortOrderFor('interviewCreatedAt'),
      render: (date?: string) =>
        date ? (
          <Text type="secondary">{new Date(date).toLocaleString()}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Clips',
      key: 'clipsGenerated',
      width: 80,
      align: 'center',
      render: (_: unknown, row: AIClipListItem) => (
        <Tag color="blue" icon={<ScissorOutlined />}>
          {row.clip?.clipsGenerated ?? 0}
        </Tag>
      ),
    },
    {
      title: 'Clip status',
      key: 'status',
      width: 120,
      render: (_: unknown, row: AIClipListItem) =>
        row.clip ? (
          <Tag color={row.clip.status === 'success' ? 'green' : 'orange'}>
            {row.clip.status?.toUpperCase()}
          </Tag>
        ) : (
          <Tag color="default">Missing</Tag>
        ),
    },
    {
      // Sorts on the newest clip candidate: the DynamoDB timestamp shown here is not sortable.
      title: 'Processed At',
      key: 'clipsCreatedAt',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sortOrderFor('clipsCreatedAt'),
      render: (_: unknown, row: AIClipListItem) => {
        const date = row.clip?.outputVideoCreatedAt;
        return date ? (
          <Text type="secondary">{new Date(date).toLocaleString()}</Text>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      render: (_: unknown, row: AIClipListItem) =>
        row.clip && row.userSlug && row.streamSlug ? (
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() =>
              history.push(
                `${AI_CLIP_DETAIL_GEN}/${encodeURIComponent(
                  row.userSlug
                )}/${encodeURIComponent(row.streamSlug)}`
              )
            }
          >
            Review
          </Button>
        ) : (
          <Tag color="default">No clip</Tag>
        ),
    },
  ];

  return (
    <div className={styles.container}>
      <PageToolbar
        leading={
          <div>
            <Text type="secondary">
              Search by interview name, host or guest name, slug, or interview
              ID.
            </Text>
          </div>
        }
      >
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by interview, name, slug, or interview ID…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          allowClear
          style={{ width: 320, maxWidth: '100%' }}
        />
        <Button onClick={handleSearch} loading={loading}>
          Search
        </Button>
        <Button
          icon={<ClearOutlined />}
          onClick={() => {
            setSearchInput('');
            setAppliedSearch('');
            setPage(1);
          }}
          disabled={!searchInput && !appliedSearch}
        >
          Clear
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() =>
            load(page, pageSize, appliedSearch, orderBy, direction)
          }
          loading={loading}
        >
          Refresh
        </Button>
      </PageToolbar>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<AIClipListItem>
        rowKey={(r) => `${r.userId}__${r.interviewId}`}
        columns={columns}
        dataSource={records}
        loading={loading}
        scroll={{ x: 1000 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (newPage, newPageSize) => {
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setPage(1);
            } else {
              setPage(newPage);
            }
          },
          showQuickJumper: false,
        }}
        onChange={handleTableChange}
        onRow={(row) => ({
          onClick: (event) => {
            const target = event.target as HTMLElement;
            if (target.closest(`.${styles.copyableCell}`)) {
              return;
            }
            if (row.clip && row.userSlug && row.streamSlug) {
              history.push(
                `${AI_CLIP_DETAIL_GEN}/${encodeURIComponent(
                  row.userSlug
                )}/${encodeURIComponent(row.streamSlug)}`
              );
            }
          },
          className: styles.tableRow,
        })}
      />
    </div>
  );
};

export default AIClips;
